import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  X, 
  Calendar, 
  Clock, 
  User, 
  MapPin, 
  BookOpen, 
  Layers, 
  AlertCircle, 
  Users as UsersIcon, 
  Printer, 
  Eye, 
  EyeOff, 
  GraduationCap,
  SlidersHorizontal,
  Building2
} from 'lucide-react';

const ClassTimetableModal = ({ timetable, classInfo, onClose, departmentInfo }) => {
  const [viewMode, setViewMode] = useState('actual');
  const [selectedDay, setSelectedDay] = useState(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Get the actual timetable data - handle different structures
  const currentTimetable = useMemo(() => {
    if (!timetable) return null;
    if (timetable.timetable) {
      return viewMode === 'actual' ? timetable.timetable : timetable.baseTimetable;
    }
    if (timetable.week) {
      return viewMode === 'actual' ? timetable : null;
    }
    return null;
  }, [timetable, viewMode]);

  const daysOfWeek = useMemo(() => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], []);

  const sortEntriesByTime = (entries) => {
    if (!entries || !Array.isArray(entries)) return [];
    return [...entries].sort((a, b) => {
      const timeA = a.timeSlot?.originalStart || a.timeSlot?.start || '00:00';
      const timeB = b.timeSlot?.originalStart || b.timeSlot?.start || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  const getDayEntries = useCallback((dayName) => {
    if (!currentTimetable || !currentTimetable.week) return [];
    const day = currentTimetable.week.find(d => d.dayName === dayName);
    if (!day) return [];
    return sortEntriesByTime(day.entries || []);
  }, [currentTimetable]);

  const hasDayEntries = useCallback((dayName) => {
    const entries = getDayEntries(dayName);
    return entries && entries.length > 0;
  }, [getDayEntries]);

  const hasDayOverrides = (dayName) => {
    if (!currentTimetable || !currentTimetable.week) return false;
    const day = currentTimetable.week.find(d => d.dayName === dayName);
    return day && day.hasOverrides;
  };

  const isToday = (dayName) => {
    if (!currentTimetable || !currentTimetable.week) return false;
    const day = currentTimetable.week.find(d => d.dayName === dayName);
    return day && day.isToday;
  };

  const getTeachingSessionCount = (dayName) => {
    const entries = getDayEntries(dayName);
    return entries.filter(e => e.isTeachingSession).length;
  };

  const summaryStats = useMemo(() => {
    if (!currentTimetable) return {};
    return currentTimetable.summary || {};
  }, [currentTimetable]);

  const weekInfo = useMemo(() => {
    if (!currentTimetable) return {};
    return currentTimetable.weekInfo || {};
  }, [currentTimetable]);

  const getClassContext = () => {
    if (!classInfo) return '';
    if (classInfo.isSemesterBased && classInfo.semester) {
      return `Semester ${classInfo.semester.number}`;
    } else if (classInfo.term) {
      return classInfo.term.name;
    }
    return '';
  };

  const getFacultyName = (entry) => {
    if (entry.facultyDetails?.name) return entry.facultyDetails.name;
    if (entry.facultyName) return entry.facultyName;
    return null;
  };

  const getSubjectName = (entry) => {
    if (entry.subjectName && entry.subjectName !== 'Teaching Session') return entry.subjectName;
    if (entry.type === 'Break') return entry.label || 'Break';
    if (entry.type === 'Custom') return entry.label || 'Custom Session';
    if (entry.type === 'Elective/Language Group') {
      return entry.electiveGroupName || entry.groupDetails?.name || 'Group Session';
    }
    return null;
  };

  const getEntryType = (entry) => {
    if (entry.isBreak) return 'break';
    if (entry.isCustomSession) return 'custom';
    if (entry.isLab) return 'lab';
    if (entry.isOverride) return 'override';
    if (entry.type === 'Elective/Language Group') return 'group';
    return 'theory';
  };

  const getTypeClasses = (type) => {
    const types = {
      theory: 'border-l-4 border-emerald-500 bg-white hover:bg-emerald-50/30',
      lab: 'border-l-4 border-blue-500 bg-blue-50/20 hover:bg-blue-50/40',
      break: 'border-l-4 border-gray-300 bg-gray-50/50 hover:bg-gray-50/70',
      custom: 'border-l-4 border-purple-500 bg-purple-50/20 hover:bg-purple-50/40',
      override: 'border-l-4 border-amber-500 bg-amber-50/30 hover:bg-amber-50/50',
      group: 'border-l-4 border-teal-500 bg-teal-50/20 hover:bg-teal-50/40'
    };
    return types[type] || types.theory;
  };

  const getBadgeColor = (type) => {
    const colors = {
      theory: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      lab: 'bg-blue-100 text-blue-700 border-blue-200',
      break: 'bg-gray-100 text-gray-600 border-gray-200',
      custom: 'bg-purple-100 text-purple-700 border-purple-200',
      override: 'bg-amber-100 text-amber-700 border-amber-200',
      group: 'bg-teal-100 text-teal-700 border-teal-200'
    };
    return colors[type] || colors.theory;
  };

  const getBadgeLabel = (type) => {
    const labels = {
      theory: 'Theory',
      lab: 'Lab',
      break: 'Break',
      custom: 'Custom',
      override: 'Modified',
      group: 'Group'
    };
    return labels[type] || 'Session';
  };

  const getTypeIcon = (type) => {
    const icons = {
      theory: <BookOpen className="w-3.5 h-3.5 text-emerald-500" />,
      lab: <Layers className="w-3.5 h-3.5 text-blue-500" />,
      break: <Clock className="w-3.5 h-3.5 text-gray-400" />,
      custom: <Layers className="w-3.5 h-3.5 text-purple-500" />,
      override: <AlertCircle className="w-3.5 h-3.5 text-amber-500" />,
      group: <UsersIcon className="w-3.5 h-3.5 text-teal-500" />
    };
    return icons[type] || <BookOpen className="w-3.5 h-3.5 text-gray-400" />;
  };

  const formatTimeDisplay = (timeSlot) => {
    if (!timeSlot) return '';
    return `${timeSlot.start || ''} - ${timeSlot.end || ''}`;
  };

  const getDayDotColor = (dayName) => {
    if (isToday(dayName)) return 'bg-emerald-500';
    if (hasDayOverrides(dayName)) return 'bg-amber-500';
    if (hasDayEntries(dayName)) return 'bg-green-500';
    return 'bg-gray-300';
  };

  const firstDayWithEntries = useMemo(() => {
    for (const day of daysOfWeek) {
      if (hasDayEntries(day)) return day;
    }
    return null;
  }, [daysOfWeek, hasDayEntries]);

  useEffect(() => {
    if (!selectedDay && firstDayWithEntries) {
      setSelectedDay(firstDayWithEntries);
    }
  }, [selectedDay, firstDayWithEntries]);

  // Get department name
  const departmentName = departmentInfo?.name || 'Department';

  if (!currentTimetable || !currentTimetable.week || currentTimetable.week.length === 0) {
    return (
      <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              {departmentName} - Class Timetable
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center p-8 text-center">
            <div>
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-700">No timetable layout loaded</p>
              <p className="text-xs text-gray-400 mt-1">There are no calendar entries configured yet for this class.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] sm:h-[85vh] flex flex-col overflow-hidden">
        
        {/* ===== HEADER BAR ===== */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate flex items-center gap-1.5">
                <GraduationCap className="w-5 h-5 text-emerald-600 hidden sm:inline" />
                {classInfo?.name || 'Class Timetable'}
              </h2>
              {summaryStats.daysWithOverrides > 0 && (
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200 flex items-center gap-0.5">
                  <AlertCircle className="w-2.5 h-2.5" /> Adjusted
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {departmentName} • {classInfo?.academicYear?.name || ''} {getClassContext()} • Week: {weekInfo.startDate || 'N/A'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-xl transition-colors text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ===== QUICK METRIC BAR & FILTERS ===== */}
        <div className="bg-emerald-50/50 border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0 gap-4 overflow-x-auto hide-scrollbar">
          <div className="flex items-center gap-4 text-[11px] font-semibold text-gray-600 whitespace-nowrap">
            <span>Total Slots: <strong className="text-gray-900">{summaryStats.totalSessions || 0}</strong></span>
            <span>Teaching: <strong className="text-green-700">{summaryStats.teachingSessions || 0}</strong></span>
            {summaryStats.daysWithOverrides > 0 && (
              <span>Modified: <strong className="text-amber-600">{summaryStats.daysWithOverrides}</strong></span>
            )}
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex bg-white rounded-lg border border-gray-200 p-0.5 shadow-xs">
              <button
                onClick={() => setViewMode('actual')}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all flex items-center gap-1 ${
                  viewMode === 'actual' ? 'bg-emerald-600 text-white shadow-xs' : 'text-gray-600'
                }`}
              >
                <Eye className="w-3 h-3" /> Actual
              </button>
              <button
                onClick={() => setViewMode('base')}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all flex items-center gap-1 ${
                  viewMode === 'base' ? 'bg-emerald-600 text-white shadow-xs' : 'text-gray-600'
                }`}
              >
                <EyeOff className="w-3 h-3" /> Original
              </button>
            </div>
            <button onClick={() => window.print()} className="p-1.5 bg-white text-gray-500 border border-gray-200 rounded-lg hover:text-emerald-600 transition-colors hidden sm:block">
              <Printer className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ===== GRID CONTENT CORE ===== */}
        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden relative">
          
          {/* DESKTOP LAYOUT DAYS BAR */}
          <div className="hidden sm:block w-44 border-r border-gray-200 bg-gray-50/50 p-3 overflow-y-auto">
            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">Days List</span>
            <div className="space-y-1">
              {daysOfWeek.map((day) => {
                const hasEntries = hasDayEntries(day);
                const isActive = selectedDay === day;
                const teachingCount = getTeachingSessionCount(day);
                
                return (
                  <button
                    key={day}
                    disabled={!hasEntries}
                    onClick={() => setSelectedDay(day)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      isActive ? 'bg-emerald-600 text-white shadow-sm' : hasEntries ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed opacity-40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white' : getDayDotColor(day)}`} />
                        {day}
                      </span>
                      {isToday(day) && <span className={`text-[8px] font-bold px-1 rounded ${isActive ? 'bg-emerald-700 text-white' : 'bg-emerald-100 text-emerald-700'}`}>Now</span>}
                    </div>
                    {hasEntries && <span className={`block text-[10px] mt-0.5 ml-3 ${isActive ? 'text-emerald-200' : 'text-gray-400 font-normal'}`}>{teachingCount} Classes</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* MOBILE VIEW INTERACTION TRIGGER TAB */}
          <div className="sm:hidden bg-white border-b border-gray-200 p-3 flex justify-between items-center flex-shrink-0">
            <div>
              <span className="text-[10px] text-gray-400 block font-medium">Selected Frame</span>
              <span className="text-xs font-bold text-gray-800 flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${getDayDotColor(selectedDay)}`} />
                {selectedDay || 'Select a day'}
              </span>
            </div>
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              className="px-3 py-1.5 bg-emerald-50 text-emerald-700 font-bold rounded-xl text-xs flex items-center gap-1 border border-emerald-100"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" /> Day Switcher
            </button>
          </div>

          {/* SESSIONS VIEW PORT CONTAINER */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/40">
            {selectedDay && hasDayEntries(selectedDay) ? (
              getDayEntries(selectedDay).map((entry, index) => {
                const type = getEntryType(entry);
                const typeClasses = getTypeClasses(type);
                const badgeColor = getBadgeColor(type);
                const badgeLabel = getBadgeLabel(type);
                const icon = getTypeIcon(type);
                const facultyName = getFacultyName(entry);
                const subjectName = getSubjectName(entry);

                return (
                  <div key={index} className={`rounded-xl p-4 border bg-white shadow-xs ${typeClasses}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap text-xs font-semibold">
                      <div className="flex items-center gap-2 flex-wrap">
                        {icon}
                        <span className="font-mono text-gray-700 bg-gray-100/80 px-2 py-0.5 rounded-md">
                          {formatTimeDisplay(entry.timeSlot)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider border ${badgeColor}`}>
                          {badgeLabel}
                        </span>
                        {entry.isOverride && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded border border-amber-200">
                            Modified
                          </span>
                        )}
                      </div>
                    </div>

                    {subjectName && entry.type !== 'Break' && entry.type !== 'Custom' && (
                      <h4 className="mt-2.5 font-bold text-gray-900 text-sm sm:text-base leading-snug">
                        {subjectName}
                        {entry.subjectCode && <span className="text-xs text-gray-400 font-normal ml-1">({entry.subjectCode})</span>}
                      </h4>
                    )}

                    {(entry.type === 'Break' || entry.type === 'Custom') && entry.label && (
                      <h4 className="mt-2.5 font-bold text-gray-800 text-sm flex items-center gap-1">
                        {entry.label}
                      </h4>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-2 text-xs text-gray-600">
                      {entry.isTeachingSession && !entry.isBreak && !entry.isCustomSession && facultyName && (
                        <div className="flex items-center gap-1.5">
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium truncate"><span className="text-gray-400">Teacher:</span> {facultyName}</span>
                        </div>
                      )}
                      {entry.room && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium truncate"><span className="text-gray-400">Room:</span> {entry.room}</span>
                        </div>
                      )}
                      {entry.batchDetails && (
                        <div className="flex items-center gap-1.5">
                          <UsersIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium truncate"><span className="text-gray-400">Target Group:</span> {entry.batchDetails.name}</span>
                        </div>
                      )}
                      {entry.className && entry.className !== classInfo?.name && (
                        <div className="flex items-center gap-1.5">
                          <GraduationCap className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium truncate"><span className="text-gray-400">Class:</span> {entry.className}</span>
                        </div>
                      )}
                    </div>

                    {entry.groupDetails && (
                      <div className="mt-3 p-2.5 bg-gray-50 rounded-xl space-y-2 border border-gray-100 text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-gray-700">{entry.groupDetails.name || 'Elective Container'}</span>
                          <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.2 rounded font-medium">
                            {entry.groupDetails.category}
                          </span>
                        </div>
                        <div className="space-y-2 pl-1">
                          {entry.groupDetails.subjects?.map((subject, idx) => (
                            <div key={idx} className="border-l-2 border-emerald-200 pl-2 py-0.5 space-y-0.5">
                              <p className="font-bold text-gray-800">{subject.subjectName}</p>
                              {subject.facultyName && <p className="text-gray-500">Teacher: {subject.facultyName}</p>}
                              {subject.subGroups?.map((sg, sgIdx) => (
                                <div key={sgIdx} className="text-gray-400 text-[11px] flex flex-wrap gap-2 mt-0.5">
                                  <span className="text-gray-600 font-medium">{sg.subGroupName || `Sub ${sgIdx + 1}`}</span>
                                  {sg.facultyName && <span>• {sg.facultyName}</span>}
                                  {sg.room && <span>• Rm {sg.room}</span>}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400 bg-white rounded-xl border border-gray-100">
                <Calendar className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-sm font-semibold text-gray-700">No layout planned</p>
                <p className="text-xs px-4">There are no classes mapped for this selected view panel.</p>
              </div>
            )}
          </div>

          {/* ===== MOBILE DRAW PANEL ELEMENT APPROACH ===== */}
          {isMobileDrawerOpen && (
            <div className="sm:hidden fixed inset-0 z-[999999] bg-black/50 backdrop-blur-xs flex items-end">
              <div className="absolute inset-0" onClick={() => setIsMobileDrawerOpen(false)} />
              <div className="relative w-full bg-white rounded-t-2xl p-4 space-y-4 max-h-[60vh] overflow-y-auto z-10 animate-slide-up">
                <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-1" />
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Select Weekday View</h3>
                  <button onClick={() => setIsMobileDrawerOpen(false)} className="px-2.5 py-1 bg-gray-100 rounded-lg text-xs font-bold text-gray-500">
                    Close
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {daysOfWeek.map((day) => {
                    const hasEntries = hasDayEntries(day);
                    const isActive = selectedDay === day;
                    return (
                      <button
                        key={day}
                        disabled={!hasEntries}
                        onClick={() => {
                          setSelectedDay(day);
                          setIsMobileDrawerOpen(false);
                        }}
                        className={`w-full p-3 rounded-xl text-left text-xs font-bold flex items-center justify-between border ${
                          isActive 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                            : hasEntries 
                              ? 'bg-white text-gray-800 border-gray-200 active:bg-gray-50' 
                              : 'bg-gray-50 text-gray-300 border-gray-100 opacity-40 cursor-not-allowed'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${getDayDotColor(day)}`} />
                          {day}
                        </span>
                        <span className="text-[10px] font-normal text-gray-400">({getDayEntries(day).length} slots)</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ===== FOOTER LEGEND LABELS ===== */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-medium text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-white border border-gray-300 border-l-4 border-l-emerald-500" /> Theory</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-blue-50/20 border border-blue-200 border-l-4 border-l-blue-500" /> Lab</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-teal-50/20 border border-teal-200 border-l-4 border-l-teal-500" /> Group</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-50/30 border border-amber-200 border-l-4 border-l-amber-500" /> Modified</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-gray-50 border border-gray-200 border-l-4 border-l-gray-300" /> Break</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ClassTimetableModal;